import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Button, IconButton, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createWebhook, getWebhook, updateWebhook } from '../../../../services/manage/webhook';
import { RootState } from '../../../../store';
import ContentLayout from '../../../Layout/Content';
import { showDialog, showToast } from '../../../utils/notifications';

interface WebhookFormData {
    url: string;
    name: string;
}

const CreateWebhook = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const region = useSelector((state: RootState) => state.mainReducer.region);
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const isManager = authContext?.is_group_manager || authContext?.role === 'Owner' || authContext?.role === 'SubOwner';
    const [isEdit, setIsEdit] = useState(false);
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm<WebhookFormData>({
        defaultValues: {
            url: '',
            name: '',
        },
    });

    useEffect(() => {
        if(!isManager) {
            showDialog({
                title: t('manage_webhook_permission_dialog'),
                content: t('manage_webhook_permission_dialog_description'),
                okText: t('ok'),
                onOk: () => {
                    navigate('/settings/notifications');
                },
                cancelButtonProps: {
                    hidden: true,
                },
            });
            return;
        }
        const fetchWebhook = async () => {
            const response = await getWebhook(region, authContext.token, id as string);
            if (response?.result?.code === '0000') {
                setValue('url', response.data.url || '');
                setValue('name', response.data.name || '');
                setIsEdit(true);
            }
        };
        if (id) fetchWebhook();
    }, [id, region, authContext.token, setValue]);

    const onSubmit = async (data: WebhookFormData) => {
        const params = JSON.stringify(data);
        const response = isEdit ? await updateWebhook(region, authContext.token, id as string, params) : await createWebhook(region, authContext.token, params);
        if (response?.result?.code === '0000') {
            showToast('success', isEdit ? t('updated_webhook_success') : t('created_webhook_success'));
            navigate('/settings/webhook');
        } else {
            showToast('error', isEdit ? t('updated_webhook_failed') : t('created_webhook_failed'));
        }
    };

    return (
        <ContentLayout fullWidth={true} position="relative" overflow="hidden">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                        <IconButton onClick={() => navigate('/settings/webhook')}>
                            <KeyboardArrowLeftIcon />
                        </IconButton>
                        <div>
                            <div className="text-sm text-gray-500">
                                {t('setting')} / {t('manage_webhook')}
                            </div>
                            <div className="text-2xl font-medium">{isEdit ? t('edit_webhook') : t('create_webhook')}</div>
                        </div>
                    </div>
                    <Button type="submit" variant="contained" color="primary">
                        {t('save')}
                    </Button>
                </div>
                <div className="mt-10 space-y-3 max-w-[480px]">
                    <h2 className="text-lg font-medium">{t('webhook_url')}</h2>
                    <p className="text-sm text-gray-500">{t('webhook_url_description')}</p>
                    <TextField
                        size="small"
                        fullWidth
                        label={t('webhook_url')}
                        placeholder={t('webhook_url_placeholder')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        {...register('url', {
                            required: t('webhook_url_required'),
                        })}
                        error={!!errors.url}
                        helperText={errors.url?.message}
                    />
                </div>
                <div className="mt-10 space-y-3 max-w-[480px]">
                    <h2 className="text-lg font-medium">{t('channel_name')}</h2>
                    <p className="text-sm text-gray-500">{t('channel_name_description')}</p>
                    <TextField
                        size="small"
                        fullWidth
                        label={t('channel_name')}
                        placeholder={t('channel_name_placeholder')}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        {...register('name', {
                            required: t('channel_name_required'),
                        })}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />
                </div>
            </form>
        </ContentLayout>
    );
};

export default CreateWebhook;
